import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Select, Button, message, Alert, Spin, TimePicker } from 'antd';
import { getStudents } from '../../api/students';
import { getTerms, getClassrooms } from '../../api/settings';
import { enrollStudent } from '../../api/schedule';
import { formatStudentLabel } from '../../utils/studentLabel';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { termsCoveringDate, pickTermForDate, noTermMessage } from '../../utils/termForDate';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

// Dönemi kim seçebilir: yönetici seçer, öğretmen seçmez (kullanıcı kararı).
// Öğretmende dönem seans tarihinden otomatik belirlenir ve ekranda yazar.
const TERM_PICKER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const TYPE_OPTIONS = [
  { value: 'regular',             label: 'Normal — Her hafta' },
  { value: 'one_time',            label: 'Tek Seferlik — Sadece o hafta' },
  { value: 'trial',               label: 'Deneme' },
  { value: 'makeup',              label: 'Telafi' },
  { value: 'makeup_uncontrolled', label: 'Kontrolsüz Telafi — Geçmiş dönem hakkı' },
];

// Öğretmen (girişte karşılama) sadece deneme/telafi kaydı açabilir — kalıcı
// program değişikliği (regular/one_time) şube müdürüne kalır. Backend de
// aynı kısıtı ayrıca uyguluyor, bu sadece UI seviyesinde.
const TEACHER_ALLOWED_TYPES = ['trial', 'makeup', 'makeup_uncontrolled'];

const getAllowedTypes = (student, isTeacher) => {
  let types = TYPE_OPTIONS;
  if (!student) {
    types = isTeacher ? types.filter(t => TEACHER_ALLOWED_TYPES.includes(t.value)) : types;
    return types;
  }
  types = student.status === 'candidate'
    ? types.filter(t => t.value === 'trial')
    : types.filter(t => t.value !== 'trial');
  if (isTeacher) types = types.filter(t => TEACHER_ALLOWED_TYPES.includes(t.value));
  return types;
};

const EnrollStudentModal = ({ open, onClose, onSuccess, slot, date, branchId, initialTime }) => {
  const { user } = useAuthStore();
  const isTeacher = user?.role === 'teacher';
  const canPickTerm = TERM_PICKER_ROLES.includes(user?.role);
  const [form] = Form.useForm();
  const [submitting, setSubmitting]           = useState(false);
  const [students, setStudents]               = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingData, setLoadingData]         = useState(false);
  // Seans tarihini KAPSAYAN aktif dönemler. Eskiden tek bir "activeTerm"
  // vardı ve o da listenin ilkiydi (= en geç başlayan dönem), yani yeni bir
  // dönem açılır açılmaz bütün kayıtlar sessizce ona gidiyordu.
  const [termOptions, setTermOptions]         = useState([]);
  const [classrooms, setClassrooms]           = useState([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const searchTimer = useRef(null);

  const isNewSlot = !slot;

  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedStudent(null);
      setStudents([]);
      setTermOptions([]);

      if (isNewSlot) {
        const startDj = initialTime ? dayjs(initialTime, 'HH:mm') : null;
        const endDj   = startDj ? startDj.add(1, 'hour') : null;
        form.setFieldsValue({ start_time: startDj, end_time: endDj });

        if (branchId) {
          setLoadingClassrooms(true);
          // exclude_archive: bkz. StudentEnrollmentsTab — arşiv dersliklerine
          // yeni ders açılmamalı, onlar geçmiş kayıt arşivi.
          getClassrooms({ branch_id: branchId, per_page: 100, is_active: 1, exclude_archive: 1 })
            .then(res => setClassrooms(res.data?.data || res.data || []))
            .catch(() => {})
            .finally(() => setLoadingClassrooms(false));
        }
      }
    }
  }, [open]);

  const handleStudentSearch = (value) => {
    clearTimeout(searchTimer.current);
    if (!value || value.trim().length < 2) { setStudents([]); return; }
    searchTimer.current = setTimeout(async () => {
      setLoadingStudents(true);
      try {
        const res = await getStudents({ search: value.trim(), per_page: 30, branch_id: branchId || undefined, status: ['active', 'candidate'] });
        setStudents(res.data?.data || res.data || []);
      } catch {
        message.error('Öğrenci araması başarısız.');
      } finally {
        setLoadingStudents(false);
      }
    }, 300);
  };

  const handleStudentChange = async (val) => {
    const student = students.find(s => s.id === val);
    setSelectedStudent(student);
    form.setFieldsValue({ term_id: null, type: null });
    setTermOptions([]);

    if (!student) return;

    if (student.status === 'candidate') form.setFieldValue('type', 'trial');
    if (student.status === 'active')    form.setFieldValue('type', isTeacher ? 'makeup' : 'regular');

    const studentBranchId = student.branch_id || branchId;
    setLoadingData(true);
    try {
      // per_page yükseltildi: aynı şubede birden fazla aktif dönem olabiliyor
      // ve doğru olan listenin dışında kalabiliyordu.
      const termRes = await getTerms({ branch_id: studentBranchId, is_active: 1, per_page: 100 });
      const termList = (termRes.data?.data || termRes.data || []).filter(t => !t.is_deleted);
      if (termList.length === 0) {
        message.warning('Bu öğrencinin şubesinde aktif dönem bulunamadı.');
        return;
      }

      // Dönem, kaydın yapıldığı SEANS TARİHİNE göre belirleniyor. Aktif
      // dönemlerin ilkini almak, yeni dönem eklendiği anda tüm kayıtları
      // yanlış döneme bağlıyordu.
      const kapsayan = termsCoveringDate(termList, date);
      if (kapsayan.length === 0) {
        message.warning(noTermMessage(date));
        return;
      }

      setTermOptions(kapsayan);
      form.setFieldValue('term_id', pickTermForDate(termList, date).id);
    } catch {
      message.error('Dönem bilgisi yüklenemedi.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const classroomId = isNewSlot ? values.classroom_id : slot.classroom_id;
      const startTime   = isNewSlot ? values.start_time?.format('HH:mm') : (slot.start_time || '').slice(0, 5);
      const endTime     = isNewSlot ? values.end_time?.format('HH:mm')   : (slot.end_time   || '').slice(0, 5);

      // Öğrencisiz ders oluşturma
      if (isNewSlot && !values.student_id) {
        await api.post('/lesson-sessions', {
          classroom_id: classroomId,
          date,
          start_time: startTime,
          end_time: endTime,
          type: 'normal',
        });
        message.success('Ders oluşturuldu.');
        onSuccess?.();
        onClose();
        return;
      }

      await enrollStudent({
        student_id:  values.student_id,
        term_id:     values.term_id,
        classroom_id: classroomId,
        day_of_week: dayjs(date).isoWeekday(),
        start_time:  startTime,
        end_time:    endTime,
        type:        values.type,
        start_date:  date,
        // "+ Ders Ekle" modunda o saatte ders OLMAMASI zaten beklenen durum —
        // amaç yeni ders açmak. Sunucudaki "bu saatte ders yok" uyarısını
        // burada sormak anlamsız olurdu, doğrudan onaylıyoruz.
        //
        // Var olan bir slota ekleme yapılıyorsa (isNewSlot=false) ders zaten
        // vardır, bayrağa gerek yok. Öğretmene bu mod arayüzde kapalı ve
        // sunucu da onu bayraktan bağımsız engelliyor.
        confirm:     isNewSlot || undefined,
      });

      message.success('Öğrenci derse eklendi.');
      onSuccess?.();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Kayıt yapılamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  const allowedTypes = getAllowedTypes(selectedStudent, isTeacher);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isNewSlot ? 'Yeni Ders Ekle' : 'Derse Öğrenci Ekle'}
      footer={null}
      destroyOnClose
    >
      {!isNewSlot && slot && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 text-sm text-blue-800">
          <b>{slot.classroom_name}</b> · {slot.start_time}–{slot.end_time} · {dayjs(date).locale('tr').format('dddd, DD.MM.YYYY')}
          <div className="text-xs text-blue-600 mt-0.5">
            Doluluk: {slot.total}/{slot.capacity}
            {slot.total >= slot.capacity && (
              <span className="text-red-500 ml-2 font-semibold">⚠ Derslik dolu</span>
            )}
          </div>
        </div>
      )}
      {isNewSlot && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-sm text-green-800">
          {date} · {initialTime} · Yeni ders oluşturuluyor
        </div>
      )}

      {selectedStudent && (
        <Alert
          className="mb-3"
          type={selectedStudent.status === 'candidate' ? 'warning' : 'info'}
          message={
            selectedStudent.status === 'candidate'
              ? 'Aday öğrenci — sadece Deneme eklenebilir.'
              : 'Aktif öğrenci — Normal ders eklenebilir.'
          }
          banner
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Yeni ders: derslik ve saat */}
        {isNewSlot && (
          <>
            <Form.Item name="classroom_id" label="Derslik" rules={[{ required: true, message: 'Derslik seçiniz.' }]}>
              <Select
                placeholder="Derslik seç"
                loading={loadingClassrooms}
                options={classrooms.map(c => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <div className="flex gap-3">
              <Form.Item name="start_time" label="Başlangıç" rules={[{ required: true, message: 'Saat giriniz.' }]} className="flex-1">
                <TimePicker format="HH:mm" minuteStep={30} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="end_time" label="Bitiş" rules={[{ required: true, message: 'Saat giriniz.' }]} className="flex-1">
                <TimePicker format="HH:mm" minuteStep={30} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </>
        )}

        {/* Öğrenci */}
        <Form.Item
          name="student_id"
          label={<span>Öğrenci <span className="text-gray-400 font-normal text-xs">(opsiyonel — sonradan eklenebilir)</span></span>}
        >
          <Select
            showSearch
            placeholder="En az 2 karakter yazın..."
            loading={loadingStudents}
            filterOption={false}
            onSearch={handleStudentSearch}
            onChange={handleStudentChange}
            notFoundContent={loadingStudents ? 'Aranıyor...' : 'Öğrenci bulunamadı'}
            options={students.map(s => ({
              value: s.id,
              label: formatStudentLabel(s),
              status: s.status,
              branchName: s.branch?.name || '',
            }))}
            optionRender={(opt) => (
              <div className="flex items-center justify-between gap-2">
                <span>{opt.data.label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {opt.data.branchName && (
                    <span className="text-xs text-gray-400">{opt.data.branchName}</span>
                  )}
                  <span className="text-xs font-medium" style={{ color: opt.data.status === 'candidate' ? '#fa8c16' : opt.data.status === 'passive' ? '#ff4d4f' : '#52c41a' }}>
                    {opt.data.status === 'candidate' ? 'Aday' : opt.data.status === 'passive' ? 'Pasif' : 'Aktif'}
                  </span>
                </div>
              </div>
            )}
          />
        </Form.Item>

        {/* Dönem — seans tarihinden belirlenir; yalnızca yönetici değiştirebilir */}
        <Form.Item
          name="term_id"
          label="Dönem"
          rules={[{ required: !!selectedStudent }]}
          extra={
            selectedStudent && termOptions.length > 0
              ? (canPickTerm
                  ? `${dayjs(date).format('DD.MM.YYYY')} tarihini kapsayan dönem${termOptions.length > 1 ? 'ler' : ''} listeleniyor.`
                  : `${dayjs(date).format('DD.MM.YYYY')} tarihine göre otomatik seçildi.`)
              : null
          }
        >
          {loadingData ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-1">
              <Spin size="small" /> Yükleniyor...
            </div>
          ) : (
            <Select
              placeholder={
                !selectedStudent
                  ? 'Önce öğrenci seçin'
                  : (termOptions.length ? 'Dönem seçin' : 'Bu tarihi kapsayan aktif dönem yok')
              }
              // Öğretmen dönem seçmez — dönem tarihe göre belirlenir.
              disabled={!selectedStudent || !canPickTerm}
              options={termOptions.map(t => ({ value: t.id, label: t.name }))}
            />
          )}
        </Form.Item>

        {/* Tip */}
        <Form.Item name="type" label="Tip" rules={[{ required: !!selectedStudent }]}>
          <Select
            placeholder="Tip seç"
            options={allowedTypes}
          />
        </Form.Item>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>İptal</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={!isNewSlot && slot?.total >= slot?.capacity}
          >
            {isNewSlot ? 'Ders Oluştur & Ekle' : 'Ekle'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EnrollStudentModal;
